import { useState, useEffect } from "react";

import { getTransactionsData } from "../../services/profileService";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  cancelRegisterAsAttendee,
  resendRegistrationEmail,
  viewAttendeePaymentReceipt,
} from "../../services/eventService";

export interface Event {
  id: number;
  title: string;
  datetime: string;
  location_city: string;
}

export interface Transaction {
  id: number | null;
  amount: number;
  discount_amount: number;
  donation_amount: number;
  service_amount: number;
  payment_process_amount: number;
  cancelled_at: string;
  registered_at_formatted: string;
  cancelled_at_formatted: string;
  number_of_attendees: number;
  createdAt: string;
  status: string;
  status_label: string;
  payment_id: string;
  refund_id: string;
  registration_token: string;
  user_id: number;
  type: string;
  Event: Event;
}

const useTransactionListLogic = () => {
  const { isAuthenticated, user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const res = await getTransactionsData();
      setTransactions(res.data.data);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  const [paymentReceiptLoading, setPaymentReceiptLoading] = useState(false);

  const handleShowPaymentReceipt = async (
    eventId: number,
    eventAttendeeId: number | null = null,
  ) => {
    try {
      if (paymentReceiptLoading) return;
      setPaymentReceiptLoading(true);
      if (!isAuthenticated) return;
      if (!eventId) return;
      const response = await viewAttendeePaymentReceipt(
        eventId,
        null,
        eventAttendeeId,
      );
      window.open(
        response.data.data.paymentIntent.latest_charge.receipt_url,
        "_blank",
      );
      showSuccessToast("Payment receipt opened in a new tab.");
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to fetch receipt.");
    } finally {
      setPaymentReceiptLoading(false);
    }
  };

  const [resendEmailLoading, setResendEmailLoading] = useState(false);

  const handleResendRegistrationEmail = async (
    eventId: number | null,
    eventAttendeeId: number | null,
  ) => {
    if (!eventId) return;
    if (!eventAttendeeId) return;
    try {
      setResendEmailLoading(true);
      const response = await resendRegistrationEmail(eventId, eventAttendeeId);
      showSuccessToast(response.data.message);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(
        errorResponse?.message || "Failed to cancel registration.",
      );
    } finally {
      setResendEmailLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const checkIsEventCancellationAllowed = (eventDatetime: string | null) => {
    if (!eventDatetime) return false;
    const currentDate = new Date();
    const eventDate = new Date(eventDatetime);
    const cancellationDeadline = new Date(eventDate);
    cancellationDeadline.setDate(cancellationDeadline.getDate() - 5); // 5 day before the event
    return currentDate < cancellationDeadline;
  };

  const [cancelLoading, setCancelLoading] = useState(false);

  const handleCancelAttendee = async (
    eventId: number | null,
    eventAttendeeId: number | null,
  ) => {
    if (!eventId) return;
    if (!eventAttendeeId) return;
    try {
      setLoading(true);
      const response = await cancelRegisterAsAttendee(eventId, eventAttendeeId);
      showSuccessToast(response.data.message);
      fetchTransactions();
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(
        errorResponse?.message || "Failed to cancel registration.",
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    transactions,
    loading,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
    resendEmailLoading,
    handleResendRegistrationEmail,
    checkIsEventCancellationAllowed,
    handleCancelAttendee,
    cancelLoading,
  };
};

export default useTransactionListLogic;
